<script setup lang="ts">
/**
 * New dataset: the name, the type and the first files in one confirmation.
 * POST /datasets creates the typed container; each file then becomes a project data object
 * and a member of it. The type is chosen here and never again — it decides how the platform
 * prepares the data for training, so the dialog says so once, where the choice is made.
 * Nothing about a file's reading is claimed here: admission starts when the bytes arrive.
 */
import { computed, ref } from 'vue'
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Icon from '~/components/ui/Icon.vue'
import ChoiceGrid from '~/components/ui/ChoiceGrid.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import { formatBytes } from '~/utils/dataObjects'
import { plural } from '~/utils/words'
import { DATASET_KINDS, acceptedWords, acceptsFile, acceptsOf, kindOf, matchCount, suggestKind } from '~/utils/datasetKinds'
import { useDatasetKindCapabilities } from '~/composables/useDatasetKindCapabilities'

const props = withDefaults(defineProps<{
  dataset?: string
  kind?: string
  /** The project's own files this dataset does not hold yet: a file goes into a second dataset
      without being uploaded again, which is what makes one file have one home. */
  available?: { id: string; title: string; facts: string }[]
}>(), { dataset: '', kind: '', available: () => [] })
const emit = defineEmits<{ close: []; submit: [payload: { name: string; kind: string; files: File[]; dataObjectIds: string[] }] }>()

/* Adding files to a dataset that already exists reuses this dialog: its name and type are
   settled, so only the files are asked for. */
const existing = computed(() => Boolean(props.dataset))
const name = ref('')
/* A dataset always has a type, so the first accepted one is chosen on open; the loaded files
   can move the choice, never clear it. */
const kind = ref(props.kind || DATASET_KINDS.find((entry) => entry.available)?.id || '')
type Entry = { key: string; file: File }
const entries = ref<Entry[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const taken = ref<string[]>([])
const take_ = (id: string) => { taken.value = taken.value.includes(id) ? taken.value.filter((entry) => entry !== id) : [...taken.value, id] }

function take(list: FileList | null | undefined) {
  for (const file of Array.from(list ?? [])) {
    const key = `${file.name}:${file.size}`
    if (entries.value.some((entry) => entry.key === key)) continue
    entries.value.push({ key, file })
  }
  if (fileInput.value) fileInput.value.value = ''
}
const choose = (event: Event) => take((event.target as HTMLInputElement).files)
const drop = (event: DragEvent) => take(event.dataTransfer?.files)

const names = computed(() => entries.value.map((entry) => entry.file.name))
const suggested = computed(() => { const guess = suggestKind(names.value); return guess && guess !== kind.value ? guess : null })
const current = computed(() => kindOf(kind.value))
const used = (entry: Entry) => !current.value || acceptsFile(current.value, entry.file.name)
const usedFiles = computed(() => entries.value.filter((entry) => used(entry)))
// What the file picker offers, and the hint under it, come from the server's answer —
// the dialog states no accept list of its own (W8-WALK).
useDatasetKindCapabilities()
const accept = computed(() => (current.value ? acceptsOf(current.value) : DATASET_KINDS.filter((entry) => entry.available).flatMap((entry) => acceptsOf(entry))).join(','))

/* Only the types that can be chosen take a tile; the ones the platform does not accept yet are
   named once, in one line under them, instead of filling the grid with rows nobody can press
   (W9-PORTAL-UX, U10). */
const types = computed(() => DATASET_KINDS.filter((entry) => entry.available).map((entry) => ({
  id: entry.id,
  title: entry.label,
  icon: entry.icon,
  facts: entries.value.length ? `${matchCount(entry, names.value)} of ${plural(entries.value.length, 'file')}` : acceptedWords(entry),
})))
const notYet = DATASET_KINDS.filter((entry) => !entry.available).map((entry) => entry.label).join(' · ')

/** Every available kind's extensions, for the hint shown before a kind is chosen. */
const everyAcceptedWord = computed(() =>
  [...new Set(DATASET_KINDS.filter((entry) => entry.available).flatMap((entry) => acceptsOf(entry)))]
    .map((entry) => entry.slice(1))
    .join(' · '))

const fileLine = computed(() => {
  const total = entries.value.length
  if (!total) return ''
  const parts = [`${plural(total, 'file')} · ${formatBytes(entries.value.reduce((sum, entry) => sum + entry.file.size, 0))}`]
  if (current.value) {
    parts.push(`${usedFiles.value.length} used by ${current.value.label}`)
    const spare = total - usedFiles.value.length
    if (spare) parts.push(`${spare} not used`)
  }
  return parts.join(' · ')
})
const chosen = computed(() => usedFiles.value.length + taken.value.length)
const meta = computed(() => chosen.value ? plural(chosen.value, 'file') : existing.value ? props.dataset : 'name · type · files')
const ready = computed(() => existing.value ? chosen.value > 0 : Boolean(name.value.trim() && kind.value))
const submit = () => emit('submit', { name: name.value.trim(), kind: kind.value, files: usedFiles.value.map((entry) => entry.file), dataObjectIds: [...taken.value] })
</script>

<template>
  <Dialog :title="existing ? 'Add files' : 'New dataset'" :meta="meta" :width="580" @close="emit('close')">
    <template #icon><Icon name="datasets" /></template>

    <template v-if="!existing">
      <Field v-model="name" label="Name" placeholder="Support tickets 2024" />

      <div class="flex shrink-0 flex-col gap-1.5">
        <span class="flex items-center justify-between text-xs text-muted-foreground">
          Type
          <span v-if="suggested" class="num text-[length:var(--type-fine)]">the files point at {{ kindOf(suggested)?.label }}</span>
          <span v-else-if="current" class="num text-[length:var(--type-fine)]">training reads {{ current.consumes.toLowerCase() }}</span>
        </span>
        <ChoiceGrid v-model="kind" :options="types" :columns="3" />
        <span class="num text-[length:var(--type-fine)] text-muted-foreground">The type is chosen once and cannot be changed afterwards.</span>
        <span v-if="notYet" class="num text-[length:var(--type-fine)] leading-[1.4] text-muted-foreground text-pretty">Not available yet: {{ notYet }}</span>
      </div>
    </template>

    <!-- A file already in the project joins this dataset as a member; the bytes are not sent again. -->
    <div v-if="existing && available.length" class="flex min-h-0 flex-col gap-1.5">
      <span class="text-xs text-muted-foreground">Already in the project</span>
      <PickList v-slot="{ shown }" :items="available" unit="files" :chosen="taken.length" height="var(--measure-160px)">
        <Choice v-for="entry in shown" :key="entry.id" multiple :title="entry.title" :facts="entry.facts" :selected="taken.includes(entry.id)" @click="take_(entry.id)" />
      </PickList>
    </div>

    <div class="flex flex-col items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-5" @dragover.prevent @drop.prevent="drop">
      <Icon name="upload" :size="16" />
      <span class="text-xs">Drop files here, or choose them</span>
      <span class="num text-[length:var(--type-fine)] text-muted-foreground">{{ current ? acceptedWords(current) : everyAcceptedWord }}</span>
      <input ref="fileInput" type="file" multiple :accept="accept" class="hidden" @change="choose">
      <ActionLink label="Choose files" @click="fileInput?.click()"><template #icon><Icon name="folder" /></template></ActionLink>
    </div>

    <div v-if="entries.length" class="flex shrink-0 flex-col rounded-md border border-border">
      <div class="flex h-7 shrink-0 items-center gap-2 border-b border-border px-2.5">
        <span class="num min-w-0 truncate text-[length:var(--type-fine)] text-muted-foreground">{{ fileLine }}</span>
        <span class="flex-1" />
        <ActionLink label="Remove all" variant="muted" @click="entries = []"><template #icon><Icon name="x" /></template></ActionLink>
      </div>
      <div class="flex max-h-[var(--measure-160px)] shrink-0 flex-col gap-[var(--measure-7px)] overflow-auto p-2.5">
        <div v-for="entry in entries" :key="entry.key" class="flex items-center gap-2 text-xs" :class="!used(entry) && 'opacity-45'">
          <Icon name="file" :size="12" class="shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate">{{ entry.file.name }}</span>
          <span class="num shrink-0 text-[length:var(--type-meta)] text-muted-foreground">{{ formatBytes(entry.file.size) }}</span>
          <span v-if="!used(entry)" class="num shrink-0 text-[length:var(--type-meta)] text-muted-foreground">Not used</span>
          <ActionLink label="Remove" variant="muted" @click="entries = entries.filter((item) => item.key !== entry.key)"><template #icon><Icon name="x" /></template></ActionLink>
        </div>
      </div>
    </div>

    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton :label="existing ? 'Add files' : 'Create dataset'" variant="primary" :disabled="!ready" @click="submit"><template #icon><Icon name="plus" /></template></ActionButton>
    </template>
  </Dialog>
</template>
